/**
 * TabSchedule — template schedule defaults editor.
 * Edits schedule_defaults and key_events_defaults JSONB on the sim_templates row.
 * Calculated totals use the same formula as SimRunWizard Step 4.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  getTemplate, updateTemplate,
  getTemplateOrganizations, getTemplateRoles, getTemplateCountries,
  type SimTemplate, type Organization, type Role, type Country,
} from '@/lib/queries'

/* -------------------------------------------------------------------------- */
/*  Props                                                                      */
/* -------------------------------------------------------------------------- */

interface TabScheduleProps {
  templateId: string
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

/** Human-friendly labels for schedule keys. */
const SCHEDULE_LABELS: Record<string, string> = {
  default_rounds: 'Default Rounds',
  round_1_duration_min: 'Round 1 Duration (min)',
  subsequent_round_duration_min: 'Subsequent Rounds Duration (min)',
  break_duration_min: 'Break Between Rounds (min)',
  introduction_duration_min: 'Introduction Duration (min)',
  reflection_duration_min: 'Reflection Duration (min)',
  debriefing_duration_min: 'Debriefing Duration (min)',
}

/** Duration fields shown in the 2-column grid (excludes default_rounds). */
const DURATION_FIELDS = [
  'round_1_duration_min',
  'subsequent_round_duration_min',
  'break_duration_min',
  'introduction_duration_min',
  'reflection_duration_min',
  'debriefing_duration_min',
] as const

/** Fallback schedule when template has no defaults. */
const FALLBACK_SCHEDULE: Record<string, number> = {
  default_rounds: 6,
  round_1_duration_min: 80,
  subsequent_round_duration_min: 60,
  break_duration_min: 15,
  introduction_duration_min: 30,
  reflection_duration_min: 20,
  debriefing_duration_min: 30,
}

/** Capitalize first letter. */
function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/* -------------------------------------------------------------------------- */
/*  Key Event types                                                            */
/* -------------------------------------------------------------------------- */

interface KeyEventParticipant {
  role_id: string
  country_code: string
  character_name: string
}

interface KeyEvent {
  type: string
  subtype?: string
  name: string
  round: number
  country_code?: string
  organization?: string
  participants?: KeyEventParticipant[]
  nominations_round?: number
  note?: string
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export function TabSchedule({ templateId }: TabScheduleProps) {
  const [loading, setLoading] = useState(true)
  const [template, setTemplate] = useState<SimTemplate | null>(null)
  const [schedule, setSchedule] = useState<Record<string, number>>(FALLBACK_SCHEDULE)
  const [keyEvents, setKeyEvents] = useState<KeyEvent[]>([])
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [countries, setCountries] = useState<Country[]>([])

  /* ---- Load template data ---- */

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [tpl, orgs, rls, ctrs] = await Promise.all([
          getTemplate(templateId),
          getTemplateOrganizations(),
          getTemplateRoles(),
          getTemplateCountries(),
        ])
        if (cancelled) return
        setOrganizations(orgs)
        setRoles(rls)
        setCountries(ctrs)
        setTemplate(tpl)
        if (tpl) {
          const sched =
            tpl.schedule_defaults && Object.keys(tpl.schedule_defaults).length > 0
              ? tpl.schedule_defaults
              : FALLBACK_SCHEDULE
          setSchedule(sched)
          setKeyEvents(
            Array.isArray(tpl.key_events_defaults)
              ? (tpl.key_events_defaults as KeyEvent[])
              : []
          )
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load template')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [templateId])

  /* ---- Derived: rounds count ---- */

  const maxRounds = schedule.default_rounds ?? 6

  /* ---- Derived: calculated totals (same formula as SimRunWizard) ---- */

  const totals = useMemo(() => {
    const r1 = schedule.round_1_duration_min ?? 80
    const rN = schedule.subsequent_round_duration_min ?? 60
    const breakMin = schedule.break_duration_min ?? 15
    const introMin = schedule.introduction_duration_min ?? 30
    const reflectionMin = schedule.reflection_duration_min ?? 20
    const debriefMin = schedule.debriefing_duration_min ?? 30
    const netPlay = r1 + rN * Math.max(0, maxRounds - 1)
    const totalBreaks = breakMin * Math.max(0, maxRounds - 1)
    const totalEvent = introMin + netPlay + totalBreaks + reflectionMin + debriefMin
    return { netPlay, totalBreaks, totalEvent }
  }, [schedule, maxRounds])

  /* ---- Change tracking ---- */

  const hasChanges = useMemo(() => {
    if (!template) return false
    const origSched = template.schedule_defaults ?? {}
    const origEvents = template.key_events_defaults ?? []
    return (
      JSON.stringify(schedule) !== JSON.stringify(origSched) ||
      JSON.stringify(keyEvents) !== JSON.stringify(origEvents)
    )
  }, [schedule, keyEvents, template])

  /* ---- Handlers ---- */

  const updateScheduleField = useCallback((key: string, value: number) => {
    setSchedule((prev) => ({ ...prev, [key]: value }))
  }, [])

  const updateKeyEvent = useCallback((index: number, updates: Partial<KeyEvent>) => {
    setKeyEvents((prev) =>
      prev.map((ev, i) => (i === index ? { ...ev, ...updates } : ev))
    )
  }, [])

  const deleteKeyEvent = useCallback((index: number) => {
    setKeyEvents((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const addKeyEvent = useCallback((event: KeyEvent) => {
    setKeyEvents((prev) => [...prev, event])
    setShowAddEvent(false)
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      await updateTemplate(templateId, {
        schedule_defaults: schedule,
        key_events_defaults: keyEvents as unknown[],
      })
      const refreshed = await getTemplate(templateId)
      if (refreshed) setTemplate(refreshed)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  /* ---- Render: loading / error ---- */

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="font-body text-body-sm text-text-secondary">Loading schedule...</p>
      </div>
    )
  }

  if (!template) {
    return (
      <div className="py-8 text-center">
        <p className="font-body text-body-sm text-danger">Template not found.</p>
      </div>
    )
  }

  /* ---- Render ---- */

  return (
    <div className="space-y-6">
      <h3 className="font-heading text-h3 text-text-primary">Schedule Defaults</h3>

      {error && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg p-3">
          <p className="font-body text-body-sm text-danger">{error}</p>
        </div>
      )}

      {/* Number of Rounds */}
      <div>
        <label className="block font-body text-body-sm text-text-primary mb-1">
          Number of Rounds
        </label>
        <input
          type="number"
          min={1}
          max={20}
          value={maxRounds}
          onChange={(e) =>
            updateScheduleField('default_rounds', Math.max(1, parseInt(e.target.value) || 1))
          }
          className="w-24 bg-base border border-border rounded px-3 py-2 font-data text-data text-text-primary focus:outline-none focus:border-action transition-colors"
        />
      </div>

      {/* Duration fields */}
      <div>
        <h4 className="font-heading text-h3 text-text-primary mb-3">Durations</h4>
        <div className="grid grid-cols-2 gap-4">
          {DURATION_FIELDS.map((key) => (
            <div key={key}>
              <label className="block font-body text-caption text-text-secondary mb-1">
                {SCHEDULE_LABELS[key] ?? key.replace(/_/g, ' ')}
              </label>
              <input
                type="number"
                min={0}
                value={schedule[key] ?? 0}
                onChange={(e) =>
                  updateScheduleField(key, Math.max(0, parseInt(e.target.value) || 0))
                }
                className="w-full bg-base border border-border rounded px-3 py-2 font-data text-data text-text-primary focus:outline-none focus:border-action transition-colors"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Calculated totals */}
      <div className="bg-base border border-border rounded-lg p-4">
        <h4 className="font-heading text-h3 text-text-primary mb-2">Calculated Totals</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="font-body text-caption text-text-secondary">Net Play Time</p>
            <p className="font-data text-data text-text-primary">
              {totals.netPlay} min ({(totals.netPlay / 60).toFixed(1)} hrs)
            </p>
          </div>
          <div>
            <p className="font-body text-caption text-text-secondary">Total Breaks</p>
            <p className="font-data text-data text-text-primary">
              {totals.totalBreaks} min
            </p>
          </div>
          <div>
            <p className="font-body text-caption text-text-secondary">Total Event Time</p>
            <p className="font-data text-data text-action font-medium">
              {totals.totalEvent} min ({(totals.totalEvent / 60).toFixed(1)} hrs)
            </p>
          </div>
        </div>
      </div>

      {/* Key Events */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading text-h3 text-text-primary">Key Events</h4>
          <button
            onClick={() => setShowAddEvent(true)}
            className="font-body text-body-sm text-action hover:underline"
          >
            + Add Event
          </button>
        </div>

        {showAddEvent && (
          <AddEventForm
            organizations={organizations}
            roles={roles}
            maxRounds={maxRounds}
            onAdd={addKeyEvent}
            onCancel={() => setShowAddEvent(false)}
          />
        )}

        {keyEvents.length === 0 ? (
          <p className="font-body text-caption text-text-secondary">
            No key events configured in template.
          </p>
        ) : (
          <div className="space-y-2">
            {/* Sort: elections first, then org sessions, then negotiations — within each group by round */}
            {[...keyEvents].map((ev, origIdx) => ({ ev, origIdx }))
              .sort((a, b) => {
                const typeOrder = (e: KeyEvent) => e.type === 'election' ? 0 : e.subtype === 'organization_session' ? 1 : 2
                const ta = typeOrder(a.ev), tb = typeOrder(b.ev)
                if (ta !== tb) return ta - tb
                return a.ev.round - b.ev.round
              })
              .map(({ ev, origIdx }) => (
              <EventRow key={origIdx} ev={ev} index={origIdx} maxRounds={maxRounds}
                organizations={organizations} roles={roles}
                onUpdate={updateKeyEvent} onDelete={deleteKeyEvent} />
            ))}
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`font-body text-body-sm font-medium py-2 px-6 rounded transition-opacity ${
            hasChanges && !saving
              ? 'bg-action text-white hover:opacity-90'
              : 'bg-action/40 text-white/60 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Schedule'}
        </button>
        {saved && (
          <span className="font-body text-caption text-success">Saved</span>
        )}
        {hasChanges && !saving && !saved && (
          <span className="font-body text-caption text-warning">Unsaved changes</span>
        )}
      </div>
    </div>
  )
}

/* ========================================================================== */
/*  Add Event Form                                                             */
/* ========================================================================== */

/* ========================================================================== */
/*  Event Row — inline editable                                               */
/* ========================================================================== */

function EventRow({ ev, index, maxRounds, organizations, roles, onUpdate, onDelete }: {
  ev: KeyEvent; index: number; maxRounds: number
  organizations: Organization[]; roles: Role[]
  onUpdate: (index: number, updates: Partial<KeyEvent>) => void
  onDelete: (index: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(ev.name)
  const [editNote, setEditNote] = useState(ev.note ?? '')
  const [editRound, setEditRound] = useState(ev.round)
  const [editParticipants, setEditParticipants] = useState<string[]>(
    (ev.participants ?? []).map(p => p.role_id)
  )

  const isElection = ev.type === 'election'
  const isOrg = ev.subtype === 'organization_session'
  const isNeg = !isElection && !isOrg

  const orgName = ev.organization
    ? organizations.find(o => o.id === ev.organization)?.sim_name ?? ev.organization
    : ''

  const COUNTRY_ORDER = [
    'columbia','cathay','gallia','teutonia','ponte','freeland','albion',
    'sarmatia','ruthenia','persia','bharata','caribe','choson','formosa',
    'hanguk','levantia','mirage','phrygia','solaria','yamato',
  ]
  const sortedRoles = [...roles].sort((a, b) => {
    const ia = COUNTRY_ORDER.indexOf(a.country_code)
    const ib = COUNTRY_ORDER.indexOf(b.country_code)
    return ia !== ib ? ia - ib : a.character_name.localeCompare(b.character_name)
  })

  const handleSave = () => {
    const updates: Partial<KeyEvent> = { name: editName, round: editRound, note: editNote || undefined }
    if (isNeg) {
      updates.participants = editParticipants.map(rid => {
        const r = roles.find(rl => rl.id === rid)
        return { role_id: rid, country_code: r?.country_code ?? '', character_name: r?.character_name ?? rid }
      })
      updates.subtype = editParticipants.length === 2 ? 'bilateral' : 'trilateral_negotiation'
    }
    onUpdate(index, updates)
    setEditing(false)
  }

  if (!editing) {
    return (
      <div className={`border rounded px-4 py-2.5 ${isElection ? 'border-warning/20' : isOrg ? 'border-action/20' : 'border-accent/20'}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <span className="font-body text-body-sm text-text-primary font-medium">{ev.name}</span>
            {isElection && ev.round != null && (
              <span className="font-body text-caption text-text-secondary ml-2">(nominations R{ev.round - 1})</span>
            )}
            {isOrg && orgName && (
              <span className="font-body text-caption text-action/70 ml-2">[{orgName}]</span>
            )}
            {ev.participants && ev.participants.length > 0 && (
              <span className="font-body text-caption text-text-secondary ml-2">
                — {ev.participants.map(p => `${cap(p.character_name)} (${cap(p.country_code)})`).join(', ')}
              </span>
            )}
            {ev.note && (
              <span className="font-body text-caption text-text-secondary/70 ml-1">· {ev.note}</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`font-body text-caption px-2 py-0.5 rounded ${
              isElection ? 'bg-warning/10 text-warning' : isOrg ? 'bg-action/10 text-action' : 'bg-accent/10 text-accent'
            }`}>
              {isElection ? 'Election' : isOrg ? 'Org Session' : 'Negotiation'}
            </span>
            <span className="font-data text-caption text-text-primary">R{ev.round}</span>
            <button onClick={() => { setEditName(ev.name); setEditNote(ev.note ?? ''); setEditRound(ev.round); setEditParticipants((ev.participants ?? []).map(p => p.role_id)); setEditing(true) }}
              className="font-body text-caption text-action hover:underline">Edit</button>
            <button onClick={() => onDelete(index)}
              className="font-body text-caption text-danger hover:underline">Delete</button>
          </div>
        </div>
      </div>
    )
  }

  // Edit mode
  return (
    <div className="border-2 border-action/30 bg-action/5 rounded-lg px-4 py-3 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block font-body text-caption text-text-secondary mb-1">Name</label>
          <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action" />
        </div>
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">Round</label>
          <input type="number" min={0} max={maxRounds} value={editRound}
            onChange={e => setEditRound(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-data text-data text-text-primary focus:outline-none focus:border-action" />
        </div>
      </div>
      <div>
        <label className="block font-body text-caption text-text-secondary mb-1">Agenda (optional)</label>
        <input type="text" value={editNote} onChange={e => setEditNote(e.target.value)}
          placeholder="Topic or purpose"
          className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-action" />
      </div>
      {isNeg && (
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">
            Participants ({editParticipants.length} selected)
          </label>
          <div className="max-h-40 overflow-y-auto border border-border rounded p-2 space-y-0.5">
            {sortedRoles.map(role => (
              <label key={role.id}
                className={`flex items-center gap-2 px-2 py-0.5 rounded cursor-pointer hover:bg-base transition-colors ${editParticipants.includes(role.id) ? 'bg-action/10' : ''}`}>
                <input type="checkbox" checked={editParticipants.includes(role.id)}
                  onChange={() => setEditParticipants(prev => prev.includes(role.id) ? prev.filter(id => id !== role.id) : [...prev, role.id])}
                  className="accent-action" />
                <span className="font-body text-body-sm text-text-primary">{role.character_name}</span>
                <span className="font-body text-caption text-text-secondary">— {cap(role.country_code)}, {role.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={handleSave} className="bg-action text-white font-body text-caption font-medium py-1.5 px-4 rounded hover:opacity-90">Save</button>
        <button onClick={() => setEditing(false)} className="font-body text-caption text-text-secondary hover:text-text-primary px-3">Cancel</button>
      </div>
    </div>
  )
}

/* ========================================================================== */
/*  Add Event Form                                                             */
/* ========================================================================== */

const ELECTION_SUBTYPES = [
  { value: 'parliamentary_midterm', label: 'Mid-Term Parliamentary Elections' },
  { value: 'presidential', label: 'Presidential Election' },
  { value: 'wartime', label: 'Wartime Presidential Election' },
] as const

function AddEventForm({
  organizations,
  roles,
  maxRounds,
  onAdd,
  onCancel,
}: {
  organizations: Organization[]
  roles: Role[]
  maxRounds: number
  onAdd: (event: KeyEvent) => void
  onCancel: () => void
}) {
  const [eventType, setEventType] = useState<'election' | 'org_session' | 'negotiation'>('org_session')

  // Election fields (Columbia only — hardcoded per scenario)
  const [electionSubtype, setElectionSubtype] = useState('parliamentary_midterm')
  const [electionRound, setElectionRound] = useState(2)

  // Organization session fields
  const [selectedOrg, setSelectedOrg] = useState('')
  const [orgRound, setOrgRound] = useState(1)
  const [orgAgenda, setOrgAgenda] = useState('')

  // Negotiation (bilateral/multilateral) fields
  const [negTitle, setNegTitle] = useState('')
  const [negRound, setNegRound] = useState(0)
  const [negAgenda, setNegAgenda] = useState('')
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([])

  const toggleParticipant = (roleId: string) => {
    setSelectedParticipants((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  const handleSubmit = () => {
    if (eventType === 'election') {
      const subtypeLabel = ELECTION_SUBTYPES.find((s) => s.value === electionSubtype)?.label ?? electionSubtype
      onAdd({
        type: 'election',
        subtype: electionSubtype,
        name: `Columbia ${subtypeLabel}`,
        country_code: 'columbia',
        round: electionRound,
      })
    } else if (eventType === 'org_session') {
      const org = organizations.find((o) => o.id === selectedOrg)
      if (!org) return
      onAdd({
        type: 'mandatory_meeting',
        subtype: 'organization_session',
        name: `${org.sim_name} Session`,
        organization: selectedOrg,
        round: orgRound,
        note: orgAgenda || undefined,
      })
    } else {
      // Negotiation
      const participants = selectedParticipants.map((roleId) => {
        const role = roles.find((r) => r.id === roleId)
        return {
          role_id: roleId,
          country_code: role?.country_code ?? '',
          character_name: role?.character_name ?? roleId,
        }
      })
      if (participants.length < 2 || !negTitle.trim()) return
      onAdd({
        type: 'mandatory_meeting',
        subtype: participants.length === 2 ? 'bilateral' : 'trilateral_negotiation',
        name: negTitle.trim(),
        participants,
        note: negAgenda || undefined,
        round: negRound,
      })
    }
  }

  // Group roles by country for participant picker
  const COUNTRY_ORDER = [
    'columbia', 'cathay', 'gallia', 'teutonia', 'ponte', 'freeland', 'albion',
    'sarmatia', 'ruthenia', 'persia', 'bharata', 'caribe', 'choson', 'formosa',
    'hanguk', 'levantia', 'mirage', 'phrygia', 'solaria', 'yamato',
  ]
  const sortedRoles = [...roles].sort((a, b) => {
    const ia = COUNTRY_ORDER.indexOf(a.country_code)
    const ib = COUNTRY_ORDER.indexOf(b.country_code)
    if (ia !== ib) return ia - ib
    return a.character_name.localeCompare(b.character_name)
  })

  return (
    <div className="border border-action/30 bg-action/5 rounded-lg p-4 mb-4 space-y-4">
      <h4 className="font-heading text-h3 text-text-primary">Add Key Event</h4>

      {/* Event type toggle */}
      <div className="flex gap-2">
        <button onClick={() => setEventType('org_session')}
          className={`font-body text-body-sm px-3 py-1.5 rounded transition-colors ${eventType === 'org_session' ? 'bg-action/20 text-action font-medium' : 'bg-base text-text-secondary hover:text-text-primary'}`}>
          Organization Session
        </button>
        <button onClick={() => setEventType('negotiation')}
          className={`font-body text-body-sm px-3 py-1.5 rounded transition-colors ${eventType === 'negotiation' ? 'bg-accent/20 text-accent font-medium' : 'bg-base text-text-secondary hover:text-text-primary'}`}>
          Negotiation
        </button>
        <button onClick={() => setEventType('election')}
          className={`font-body text-body-sm px-3 py-1.5 rounded transition-colors ${eventType === 'election' ? 'bg-warning/20 text-warning font-medium' : 'bg-base text-text-secondary hover:text-text-primary'}`}>
          Columbia Election
        </button>
      </div>

      {eventType === 'election' ? (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block font-body text-caption text-text-secondary mb-1">Election Type</label>
            <select value={electionSubtype} onChange={(e) => setElectionSubtype(e.target.value)}
              className="w-full bg-base border border-border rounded px-3 py-2 font-body text-body-sm text-text-primary focus:outline-none focus:border-action">
              {ELECTION_SUBTYPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-body text-caption text-text-secondary mb-1">Election Round</label>
            <input type="number" min={1} max={maxRounds} value={electionRound}
              onChange={(e) => setElectionRound(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full bg-base border border-border rounded px-3 py-2 font-data text-data text-text-primary focus:outline-none focus:border-action" />
          </div>
          <div>
            <label className="block font-body text-caption text-text-secondary mb-1">Nominations Round</label>
            <div className="px-3 py-2 bg-base border border-border rounded font-data text-data text-text-secondary">
              R{Math.max(1, electionRound - 1)} (auto: election round - 1)
            </div>
          </div>
        </div>
      ) : eventType === 'org_session' ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-body text-caption text-text-secondary mb-1">Organization</label>
              <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
                className="w-full bg-base border border-border rounded px-3 py-2 font-body text-body-sm text-text-primary focus:outline-none focus:border-action">
                <option value="">Select organization...</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>{o.sim_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-body text-caption text-text-secondary mb-1">Round</label>
              <input type="number" min={0} max={maxRounds} value={orgRound}
                onChange={(e) => setOrgRound(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-full bg-base border border-border rounded px-3 py-2 font-data text-data text-text-primary focus:outline-none focus:border-action" />
            </div>
          </div>
          <div>
            <label className="block font-body text-caption text-text-secondary mb-1">Agenda (optional)</label>
            <input type="text" value={orgAgenda} onChange={(e) => setOrgAgenda(e.target.value)}
              placeholder="Topic or purpose of this session"
              className="w-full bg-base border border-border rounded px-3 py-2 font-body text-body-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-action" />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-body text-caption text-text-secondary mb-1">Title</label>
              <input type="text" value={negTitle} onChange={(e) => setNegTitle(e.target.value)}
                placeholder="e.g. Persia Framework Talks"
                className="w-full bg-base border border-border rounded px-3 py-2 font-body text-body-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-action" />
            </div>
            <div>
              <label className="block font-body text-caption text-text-secondary mb-1">Round</label>
              <input type="number" min={0} max={maxRounds} value={negRound}
                onChange={(e) => setNegRound(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-full bg-base border border-border rounded px-3 py-2 font-data text-data text-text-primary focus:outline-none focus:border-action" />
            </div>
          </div>
          <div>
            <label className="block font-body text-caption text-text-secondary mb-1">Agenda (optional)</label>
            <input type="text" value={negAgenda} onChange={(e) => setNegAgenda(e.target.value)}
              placeholder="Framework discussion, back-channel, etc."
              className="w-full bg-base border border-border rounded px-3 py-2 font-body text-body-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-action" />
          </div>
          <div>
            <label className="block font-body text-caption text-text-secondary mb-2">
              Participants ({selectedParticipants.length} selected)
            </label>
            <div className="max-h-48 overflow-y-auto border border-border rounded p-2 space-y-0.5">
              {sortedRoles.map((role) => (
                <label key={role.id}
                  className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-base transition-colors ${selectedParticipants.includes(role.id) ? 'bg-action/10' : ''}`}>
                  <input type="checkbox" checked={selectedParticipants.includes(role.id)}
                    onChange={() => toggleParticipant(role.id)} className="accent-action" />
                  <span className="font-body text-body-sm text-text-primary">{role.character_name}</span>
                  <span className="font-body text-caption text-text-secondary">— {cap(role.country_code)}, {role.title}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={handleSubmit}
          className="bg-action text-white font-body text-body-sm font-medium py-2 px-4 rounded hover:opacity-90 transition-opacity">
          Add Event
        </button>
        <button onClick={onCancel}
          className="bg-base border border-border text-text-secondary font-body text-body-sm font-medium py-2 px-4 rounded hover:bg-border/30 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  )
}
